import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  categoriesUrl, categoryByIdUrl, branchPricingUrl, branchesUrl, branchByIdUrl,
  facilityUrl, facilitySettingsUrl,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtMoney } from '../../utils/format';
import { toastSuccess, swalDangerConfirm, showAxiosError, swalWarning } from '../../utils/swal';

const emptyCat = { name: '', description: '', default_markup_percent: '' };

const Settings = ({ me }) => {
  const isSuper = me?.role === 'super_admin';

  // We let the page render for non-super users but the backend will reject
  // mutations with 403, and the sidebar already hides the link for them.
  const [tab, setTab] = useState('general');
  const [cats, setCats] = useState([]);
  const [branches, setBranches] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [facility, setFacility] = useState(null);
  const [savingMode, setSavingMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editCat, setEditCat] = useState(null); // null | { id, name, description, default_markup_percent } | 'new'
  const [catForm, setCatForm] = useState(emptyCat);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, b, p, f] = await Promise.all([
        axios.get(categoriesUrl),
        axios.get(branchesUrl),
        axios.get(branchPricingUrl).catch(() => ({ data: { results: [] } })),
        axios.get(facilityUrl).catch(() => ({ data: { facility: null } })),
      ]);
      setCats(c.data?.results || []);
      setBranches(b.data?.results || []);
      setOverrides(p.data?.results || []);
      setFacility(f.data?.facility || null);
    } finally { setLoading(false); }
  };

  const savePurchaseMode = async (mode) => {
    if (!facility || facility.purchase_mode === mode) return;
    setSavingMode(true);
    try {
      await axios.patch(facilitySettingsUrl, { purchase_mode: mode });
      setFacility({ ...facility, purchase_mode: mode });
      toastSuccess(`Purchase mode set to "${mode.replace('_', ' ')}"`);
    } catch (err) { showAxiosError(err, 'Could not save'); }
    finally { setSavingMode(false); }
  };
  useEffect(() => { load().catch(() => {}); }, []);

  // Lookup: { "branch_id-category_id": override row }
  const overrideMap = useMemo(() => {
    const m = new Map();
    overrides.forEach((o) => m.set(`${o.branch_id}-${o.category_id}`, o));
    return m;
  }, [overrides]);

  const openCreateCat = () => {
    setCatForm(emptyCat);
    setEditCat('new');
    setError(null);
  };
  const openEditCat = (c) => {
    setCatForm({
      name: c.name,
      description: c.description || '',
      default_markup_percent: c.default_markup_percent != null ? String(c.default_markup_percent) : '',
    });
    setEditCat(c);
    setError(null);
  };

  const submitCat = async () => {
    setError(null);
    if (!catForm.name.trim()) { setError('Name is required'); return; }
    const pct = catForm.default_markup_percent;
    if (pct !== '' && (isNaN(Number(pct)) || Number(pct) < 0)) {
      setError('Markup must be a non-negative number');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: catForm.name.trim(),
        description: catForm.description.trim() || null,
        default_markup_percent: pct === '' ? null : Number(pct),
      };
      if (editCat === 'new') {
        await axios.post(categoriesUrl, payload);
        toastSuccess(`Category "${payload.name}" created`);
      } else {
        await axios.patch(categoryByIdUrl(editCat.id), payload);
        toastSuccess(`Category "${payload.name}" updated`);
      }
      setEditCat(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const removeCat = async (c) => {
    if (Number(c.product_count) > 0) {
      return swalWarning(
        'Category in use',
        `${c.product_count} product(s) are still tagged with "${c.name}". Re-tag them first.`
      );
    }
    const ok = await swalDangerConfirm(`Deactivate "${c.name}"?`, 'It will no longer appear in product forms or pricing settings.');
    if (!ok) return;
    try {
      await axios.delete(categoryByIdUrl(c.id));
      toastSuccess('Category deactivated');
      load();
    } catch (err) { showAxiosError(err); }
  };

  // Per-branch override save — fires on blur or Enter.
  const saveOverride = async (branch_id, category_id, value) => {
    const v = String(value).trim();
    const markup_percent = v === '' ? null : Number(v);
    if (v !== '' && !Number.isFinite(markup_percent)) return;
    try {
      await axios.put(branchPricingUrl, { branch_id, category_id, markup_percent });
      await load();
      toastSuccess(v === '' ? 'Override cleared' : `Markup set to ${markup_percent}%`);
    } catch (err) { showAxiosError(err, 'Could not save override'); }
  };

  // Branch-level default markup save — applies across every category until a
  // per-category override is set.
  const saveBranchDefault = async (branch_id, value) => {
    const v = String(value).trim();
    const default_markup_percent = v === '' ? null : Number(v);
    if (v !== '' && !Number.isFinite(default_markup_percent)) return;
    try {
      await axios.patch(branchByIdUrl(branch_id), { default_markup_percent });
      await load();
      toastSuccess(v === '' ? 'Branch default cleared' : `Branch default set to ${default_markup_percent}%`);
    } catch (err) { showAxiosError(err, 'Could not save branch default'); }
  };

  if (!isSuper && !loading && cats.length === 0) {
    // No categories visible suggests the user really doesn't have access
    return (
      <div className="panel">
        <div className="audit-scope-banner" style={{ marginBottom: 0 }}>
          <span className="ic">🔒</span>
          <div>
            <strong>Restricted area.</strong>
            <p>Settings are managed by the super administrator. Ask them to grant you the <code>admin.settings.manage</code> permission if you need to edit categories or branch pricing.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Top tabs */}
      <div className="settings-tabs">
        <button className={`settings-tab ${tab === 'general' ? 'active' : ''}`} onClick={() => setTab('general')}>
          <span className="ic">⚙️</span> General
        </button>
        <button className={`settings-tab ${tab === 'categories' ? 'active' : ''}`} onClick={() => setTab('categories')}>
          <span className="ic">🏷️</span> Categories &amp; markup
          <span className="ct">{cats.length}</span>
        </button>
        <button className={`settings-tab ${tab === 'pricing' ? 'active' : ''}`} onClick={() => setTab('pricing')}>
          <span className="ic">💰</span> Branch pricing
          <span className="ct">{overrides.length}</span>
        </button>
      </div>

      {/* GENERAL TAB — facility-wide toggles */}
      {tab === 'general' && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>General settings</h2>
              <p className="muted-sm" style={{ margin: '2px 0 0' }}>
                Facility-wide choices that change how the whole system behaves.
              </p>
            </div>
          </div>

          <h4 className="settings-section-head">Purchase mode</h4>
          <p className="settings-section-desc">
            Choose where purchases are raised. You can change this at any time.
          </p>

          {loading || !facility ? (
            <p style={{ color: 'var(--mute)', padding: 24, textAlign: 'center' }}>Loading…</p>
          ) : (
            <div className="purchase-mode-grid">
              {[
                {
                  key: 'hq_only',
                  icon: '🏛️',
                  title: 'HQ only',
                  blurb: 'Only the head office can raise purchase orders. Stock is then moved to branches with stock transfers.',
                  pros: ['Centralised supplier negotiation', 'Single pricing chain', 'Clean audit trail'],
                },
                {
                  key: 'branch_direct',
                  icon: '🏬',
                  title: 'Branch direct',
                  blurb: 'Each branch raises its own purchase orders directly. HQ does not run procurement.',
                  pros: ['Branch autonomy', 'Faster restocking', 'No HQ bottleneck'],
                },
                {
                  key: 'both',
                  icon: '🔀',
                  title: 'Both (hybrid)',
                  blurb: 'Either path is allowed. HQ can buy and transfer, and branches can buy direct from their own suppliers.',
                  pros: ['Maximum flexibility', 'Branch-level discretion', 'HQ keeps central buying power'],
                },
              ].map((opt) => {
                const active = facility.purchase_mode === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={`purchase-mode-card ${active ? 'active' : ''}`}
                    onClick={() => savePurchaseMode(opt.key)}
                    disabled={savingMode}
                  >
                    <div className="ic">{opt.icon}</div>
                    <h3>{opt.title}{active && <span className="active-badge">Current</span>}</h3>
                    <p>{opt.blurb}</p>
                    <ul>
                      {opt.pros.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                  </button>
                );
              })}
            </div>
          )}

          <div className="settings-example" style={{ marginTop: 18 }}>
            <h4>How this affects purchases</h4>
            <p>
              When set to <strong>HQ only</strong>, branch managers will see a friendly message instead of the <em>+ New purchase</em> button — they'll be redirected to <strong>Stock Transfers</strong> to receive incoming inventory.
              When set to <strong>Branch direct</strong>, the HQ purchase form is hidden and HQ stock comes only from transfers or adjustments.
            </p>
          </div>
        </div>
      )}

      {/* CATEGORIES TAB */}
      {tab === 'categories' && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>Categories &amp; default markup</h2>
              <p className="muted-sm" style={{ margin: '2px 0 0' }}>
                Group products and set a default markup % per category. New products in that category
                start with this margin and every branch inherits it unless they override on the next tab.
              </p>
            </div>
            <div className="panel-toolbar">
              <button className="btn-primary" onClick={openCreateCat}>+ New category</button>
            </div>
          </div>

          {loading ? (
            <p style={{ color: 'var(--mute)', padding: 24, textAlign: 'center' }}>Loading…</p>
          ) : cats.length === 0 ? (
            <div className="br-empty">
              <div className="ic">🏷️</div>
              <h3>No categories yet</h3>
              <p>Create the first one to start grouping products and pricing them by margin.</p>
              <button className="btn-primary" onClick={openCreateCat}>+ Create your first category</button>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl prod-tbl compact">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="right">Default markup</th>
                    <th className="right">Products</th>
                    <th className="right">Branch overrides</th>
                    <th className="right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cats.map((c) => {
                    const overrideCount = overrides.filter((o) => o.category_id === c.id).length;
                    return (
                      <tr key={c.id}>
                        <td><strong>{c.name}</strong></td>
                        <td>{c.description || <span style={{ color: 'var(--mute-2)' }}>—</span>}</td>
                        <td className="right num">
                          {c.default_markup_percent != null
                            ? <span className="prod-margin good">{Number(c.default_markup_percent).toFixed(0)}%</span>
                            : <span style={{ color: 'var(--mute-2)' }}>not set</span>}
                        </td>
                        <td className="right num">{Number(c.product_count)}</td>
                        <td className="right num">
                          {overrideCount > 0
                            ? <span className="prod-cat-pill">{overrideCount} branch{overrideCount === 1 ? '' : 'es'}</span>
                            : <span style={{ color: 'var(--mute-2)' }}>—</span>}
                        </td>
                        <td className="right">
                          <div className="row-icons">
                            <button
                              className="icon-btn brand" onClick={() => openEditCat(c)}
                              title="Edit category" aria-label="Edit"
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M13.5 3.5a2.121 2.121 0 1 1 3 3L7 16l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              className="icon-btn danger" onClick={() => removeCat(c)}
                              title="Deactivate" aria-label="Deactivate"
                            >
                              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 6h12M8 6V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2M6 6l1 10a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1l1-10"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pricing example */}
          <div className="settings-example">
            <h4>How pricing works</h4>
            <p>
              For each branch, the effective selling price of a product is computed as:
              <code className="formula">cost × (1 + markup% ÷ 100)</code>
            </p>
            <ol>
              <li>If the branch has a <strong>specific override</strong> on the product's category — use that.</li>
              <li>Otherwise, use the <strong>branch's general default markup</strong> (set when the branch is registered or on the Branch pricing tab).</li>
              <li>Otherwise, use the <strong>category's default markup</strong>.</li>
              <li>If none of the above is set, use the product's stored <strong>selling price</strong>.</li>
            </ol>
            <p style={{ marginBottom: 0 }}>
              Example: a drug bought for <strong>GHS 20</strong> with a 25% category default sells for <strong>{fmtMoney(20 * 1.25)}</strong> system-wide,
              but if Branch A overrides to 40%, the same drug becomes <strong>{fmtMoney(20 * 1.40)}</strong> only at Branch A.
            </p>
          </div>
        </div>
      )}

      {/* BRANCH PRICING TAB */}
      {tab === 'pricing' && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <h2>Branch pricing overrides</h2>
              <p className="muted-sm" style={{ margin: '2px 0 0' }}>
                Set a different markup for any category at any branch. Leave a cell empty to fall back to the category default
                (shown faintly under the input).
              </p>
            </div>
          </div>

          {loading ? (
            <p style={{ color: 'var(--mute)', padding: 24, textAlign: 'center' }}>Loading…</p>
          ) : branches.length === 0 || cats.length === 0 ? (
            <div className="br-empty">
              <div className="ic">🏬</div>
              <h3>Set up categories and branches first</h3>
              <p>You need at least one branch and one category before you can configure per-branch pricing.</p>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl pricing-grid">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="right">Default</th>
                    {branches.map((b) => (
                      <th key={b.id} className="right">{b.name}{b.is_head_office ? ' (HQ)' : ''}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Branch general default — applies to every category unless a category override exists */}
                  <tr className="branch-default-row">
                    <td>
                      <strong>Branch general default</strong>
                      <div style={{ fontSize: 11.5, color: 'var(--mute)' }}>
                        cuts across every category
                      </div>
                    </td>
                    <td className="right"><span style={{ color: 'var(--mute-2)', fontSize: 12 }}>—</span></td>
                    {branches.map((b) => (
                      <td key={b.id} className="right">
                        <PricingCell
                          defaultPct={null}
                          currentPct={b.default_markup_percent}
                          onSave={(v) => saveBranchDefault(b.id, v)}
                        />
                      </td>
                    ))}
                  </tr>

                  {cats.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.name}</strong></td>
                      <td className="right">
                        {c.default_markup_percent != null
                          ? <span className="prod-margin ok">{Number(c.default_markup_percent).toFixed(0)}%</span>
                          : <span style={{ color: 'var(--mute-2)', fontSize: 12 }}>—</span>}
                      </td>
                      {branches.map((b) => {
                        const o = overrideMap.get(`${b.id}-${c.id}`);
                        // When this branch has no per-category override, fall back to the
                        // branch's general default (shown as placeholder), then to the
                        // category default — matches the backend pricing chain.
                        const fallbackPct = b.default_markup_percent != null
                          ? b.default_markup_percent
                          : c.default_markup_percent;
                        return (
                          <td key={b.id} className="right">
                            <PricingCell
                              defaultPct={fallbackPct}
                              currentPct={o?.markup_percent}
                              onSave={(v) => saveOverride(b.id, c.id, v)}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Category create / edit modal */}
      <Modal
        title={editCat === 'new' ? 'New category' : (editCat ? `Edit "${editCat.name}"` : '')}
        open={!!editCat}
        onClose={() => setEditCat(null)}
        width={520}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setEditCat(null)}>Cancel</button>
            <button className="btn-primary" onClick={submitCat} disabled={saving}>
              {saving ? 'Saving…' : (editCat === 'new' ? 'Create category' : 'Save changes')}
            </button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}
        <div className="f-row">
          <label className="f-label">Name *</label>
          <input
            className="f-input"
            value={catForm.name}
            onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
            placeholder="e.g. Antibiotics"
          />
        </div>
        <div className="f-row">
          <label className="f-label">Description</label>
          <input
            className="f-input"
            value={catForm.description}
            onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
            placeholder="Optional"
          />
        </div>
        <div className="f-row" style={{ marginBottom: 0 }}>
          <label className="f-label">Default markup % <span style={{ color: 'var(--mute-2)', textTransform: 'none', fontWeight: 500 }}>(optional)</span></label>
          <input
            className="f-input"
            type="number"
            step="0.01"
            min="0"
            value={catForm.default_markup_percent}
            onChange={(e) => setCatForm({ ...catForm, default_markup_percent: e.target.value })}
            placeholder="e.g. 25"
          />
          <p style={{ fontSize: 11.5, color: 'var(--mute)', margin: '6px 0 0' }}>
            Every product in this category will sell at <code>cost × (1 + markup/100)</code> system-wide,
            unless a branch overrides it.
          </p>
        </div>
      </Modal>
    </>
  );
};

// Inline editable cell for the pricing grid.
// Saves on blur / Enter; shows the category default as a placeholder so the
// admin can see what value will be used when the cell is empty.
const PricingCell = ({ defaultPct, currentPct, onSave }) => {
  const [value, setValue] = useState(currentPct != null ? String(currentPct) : '');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(currentPct != null ? String(currentPct) : '');
    setTouched(false);
  }, [currentPct]);

  const commit = () => {
    if (!touched) return;
    onSave(value);
    setTouched(false);
  };

  return (
    <div className="pricing-cell">
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        placeholder={defaultPct != null ? String(defaultPct) : '—'}
        onChange={(e) => { setValue(e.target.value); setTouched(true); }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
        className={currentPct != null ? 'has-override' : ''}
      />
      <span className="pct">%</span>
    </div>
  );
};

export default Settings;
